import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

/** Without a key the value is kept in memory only. */
export function useLocalStorage<T>(
  key: string | undefined,
  defaultValue: T,
): [value: T, setValue: Dispatch<SetStateAction<T>>] {
  return useStorage(localStorage, key, defaultValue);
}

/** Without a key the value is kept in memory only. */
export function useSessionStorage<T>(
  key: string | undefined,
  defaultValue: T,
): [value: T, setValue: Dispatch<SetStateAction<T>>] {
  return useStorage(sessionStorage, key, defaultValue);
}

// See: https://developer.mozilla.org/docs/Web/API/Window/storage_event
const eventName = 'storage';

function readItem<T>(
  storage: Storage,
  key: string | undefined,
  defaultValue: T,
): T {
  const item = key ? storage.getItem(key) : null;
  return item ? JSON.parse(item) : defaultValue;
}

function useStorage<T>(
  storage: Storage,
  key: string | undefined,
  defaultValue: T,
): [value: T, setValue: Dispatch<SetStateAction<T>>] {
  const [value, setValueLocally] = useState<T>(() =>
    readItem(storage, key, defaultValue),
  );

  const setValue = useCallback(
    (newValue: SetStateAction<T>) => {
      if (!key) {
        setValueLocally(newValue);
        return;
      }

      const valueToStore =
        typeof newValue === 'function'
          ? (newValue as (prev: T) => T)(readItem(storage, key, defaultValue))
          : newValue;
      storage.setItem(key, JSON.stringify(valueToStore));
      window.dispatchEvent(new StorageEvent(eventName, { key: key }));
    },
    [key],
  );

  const handler = useCallback(
    (e: StorageEvent) => {
      if (!key || e.key != key) return;
      setValueLocally(readItem(storage, key, defaultValue));
    },
    [key, defaultValue],
  );

  useEffect(() => {
    window.addEventListener(eventName, handler);

    return () => {
      window.removeEventListener(eventName, handler);
    };
  }, [handler]);

  return [value, setValue];
}
